import { createId } from '@paralleldrive/cuid2';
import { sql } from 'drizzle-orm';
import { customType, index, integer, jsonb, pgEnum, pgTable, text, timestamp, varchar, } from 'drizzle-orm/pg-core';
import { createInsertSchema, createSelectSchema, createUpdateSchema, } from 'drizzle-zod';
import { eventTypes } from '../lib/eventi/types';
// Define Enums
export const interventoStatusEnum = pgEnum('intervento_status', [
    'bozza',
    'in_corso',
    'completato',
]);
export const tipologiaCaditoiaEnum = pgEnum('tipologia_caditoia', [
    'griglia',
    'bocca_di_lupo',
    'pozzetto',
]);
export const esitoEnum = pgEnum('esito', ['positivo', 'negativo']);
export const syncDirectionEnum = pgEnum('sync_direction', ['pull', 'push']);
export const tipoEventoEnum = pgEnum('tipo_evento', eventTypes);
// The id of the transaction that last wrote a row, set by a trigger from the
// database (migration 0017). Pulls page on it (K35); it reads as a decimal string.
const xid8 = customType({
    dataType: () => 'xid8',
});
const syncXid = () => xid8('sync_xid')
    .notNull()
    .default(sql `'1'`);
// Define Tables
export const interventi = pgTable('interventi', {
    id: varchar('id')
        .primaryKey()
        .$defaultFn(() => createId()),
    id_interno: varchar('id_interno').notNull(),
    status: interventoStatusEnum('status').default('in_corso').notNull(),
    tipologia_caditoia: tipologiaCaditoiaEnum('tipologia_caditoia').notNull(),
    caditoie_num: integer('caditoie_num').notNull(),
    coordinate: varchar('coordinate'),
    indirizzo: varchar('indirizzo'),
    note_pre: text('note_pre'),
    note_post: text('note_post'),
    // Who inspected it, set once; a repair never rewrites it
    operatore_id: varchar('operatore_id'),
    data: timestamp('data', { withTimezone: true }).notNull(),
    committente_id: varchar('committente_id'),
    esito: esitoEnum('esito'),
    // The operatore of the latest repair not cancelled, folded from the events by
    // the server like status and esito; NULL reads as operatore_id
    current_operatore_id: varchar('current_operatore_id'),
    created_at: timestamp('created_at', { withTimezone: true })
        .defaultNow()
        .notNull(),
    updated_at: timestamp('updated_at', { withTimezone: true }).notNull(),
    deleted_at: timestamp('deleted_at', { withTimezone: true }),
    sync_xid: syncXid(),
}, (table) => ({
    interventiSyncXidIdx: index('interventi_sync_xid_idx').on(table.sync_xid, table.id),
    interventiCreatedAtIdx: index('interventi_created_at_idx').on(table.created_at),
    interventiUpdatedAtIdx: index('interventi_updated_at_idx').on(table.updated_at),
    interventiOperatoreUpdatedIdx: index('interventi_operatore_updated_idx').on(table.operatore_id, table.updated_at),
    interventiCommittenteStatusUpdatedIdx: index('interventi_committente_status_updated_idx').on(table.committente_id, table.status, table.updated_at),
    interventiCommittenteEsitoDataIdx: index('interventi_committente_esito_data_idx').on(table.committente_id, table.esito, table.data),
    interventiDeletedAtIdx: index('interventi_deleted_at_idx')
        .on(table.deleted_at)
        .where(sql `${table.deleted_at} IS NOT NULL`),
    interventiCurrentOperatoreUpdatedIdx: index('interventi_current_operatore_updated_idx').on(table.current_operatore_id, table.updated_at),
    interventiIdInternoIdx: index('interventi_id_interno_idx').on(table.id_interno),
}));
// sync_xid stays on the server: the UI's types and local database layout are
// derived from these, and must not change (K35)
export const interventiSelectSchema = createSelectSchema(interventi).omit({
    sync_xid: true,
});
export const interventiInsertSchema = createInsertSchema(interventi);
export const interventUpdateSchema = createUpdateSchema(interventi);
// What happened to an intervento, never updated: its status, esito and current
// operatore are folded from these. Phones get them inside the intervento (sync.pull)
// and send new ones with it (sync.push). Clients never read the table directly, so
// RLS is on with no policies: the Data API sees nothing
export const interventi_eventi = pgTable('interventi_eventi', {
    id: varchar('id').primaryKey(),
    intervento_id: varchar('intervento_id')
        .notNull()
        .references(() => interventi.id, { onDelete: 'cascade' }),
    tipo: tipoEventoEnum('tipo').notNull(),
    operatore_id: varchar('operatore_id').notNull(),
    riparazione_id: varchar('riparazione_id').references(() => interventi_eventi.id, { onDelete: 'cascade' }),
    esito: esitoEnum('esito'),
    note: text('note'),
    // When it happened, by the clock of the device that recorded it
    created_at: timestamp('created_at', { withTimezone: true }).notNull(),
    // When the server received it, for the log: never folded, never sent to
    // phones. NULL for events stored before it existed
    recorded_at: timestamp('recorded_at', { withTimezone: true }).defaultNow(),
}, (table) => ({
    interventiEventiInterventoCreatedIdx: index('interventi_eventi_intervento_created_idx').on(table.intervento_id, table.created_at),
    interventiEventiRiparazioneIdx: index('interventi_eventi_riparazione_idx').on(table.riparazione_id),
    interventiEventiOperatoreCreatedIdx: index('interventi_eventi_operatore_created_idx').on(table.operatore_id, table.created_at),
    interventiEventiTipoCreatedIdx: index('interventi_eventi_tipo_created_idx').on(table.tipo, table.created_at),
})).enableRLS();
export const foto_interventi = pgTable('foto_interventi', {
    id: varchar('id')
        .primaryKey()
        .$defaultFn(() => createId()),
    s3_key: varchar('s3_key'),
    mime_type: varchar('mime_type'),
    intervento_id: varchar('intervento_id'),
    categoria: varchar('categoria'),
    operatore_id: varchar('operatore_id'),
    committente_id: varchar('committente_id'),
    // The repair this photo proves; NULL: a photo of the inspection
    riparazione_id: varchar('riparazione_id').references(() => interventi_eventi.id),
    created_at: timestamp('created_at', { withTimezone: true })
        .defaultNow()
        .notNull(),
    updated_at: timestamp('updated_at', { withTimezone: true }).notNull(),
    deleted_at: timestamp('deleted_at', { withTimezone: true }),
    sync_xid: syncXid(),
}, (table) => ({
    fotoInterventiSyncXidIdx: index('foto_interventi_sync_xid_idx').on(table.sync_xid, table.id),
    fotoInterventiCreatedAtIdx: index('foto_interventi_created_at_idx').on(table.created_at),
    fotoInterventiUpdatedAtIdx: index('foto_interventi_updated_at_idx').on(table.updated_at),
    fotoInterventiOperatoreUpdatedIdx: index('foto_interventi_operatore_updated_idx').on(table.operatore_id, table.updated_at),
    fotoInterventiCommittenteUpdatedIdx: index('foto_interventi_committente_updated_idx').on(table.committente_id, table.updated_at),
    fotoInterventiDeletedAtIdx: index('foto_interventi_deleted_at_idx')
        .on(table.deleted_at)
        .where(sql `${table.deleted_at} IS NOT NULL`),
    fotoInterventiInterventoIdx: index('foto_interventi_intervento_idx').on(table.intervento_id),
    fotoInterventiRiparazioneIdx: index('foto_interventi_riparazione_idx').on(table.riparazione_id),
}));
export const fotoInterventiSelectSchema = createSelectSchema(foto_interventi).omit({ sync_xid: true });
export const fotoInterventiInsertSchema = createInsertSchema(foto_interventi);
export const fotoInterventiUpdateSchema = createUpdateSchema(foto_interventi);
export const roleEnum = pgEnum('role', ['admin', 'operatore']);
export const utenti = pgTable('utenti', {
    user_id: varchar('user_id').primaryKey(),
    username: varchar('username').notNull().unique(),
    first_name: varchar('first_name').notNull(),
    last_name: varchar('last_name').notNull(),
    role: roleEnum('role').notNull().default('operatore'),
    created_at: timestamp('created_at', { withTimezone: true })
        .defaultNow()
        .notNull(),
    updated_at: timestamp('updated_at', { withTimezone: true }).notNull(),
    deleted_at: timestamp('deleted_at', { withTimezone: true }),
    deactivated_at: timestamp('deactivated_at', { withTimezone: true }),
    sync_xid: syncXid(),
}, (table) => ({
    utentiSyncXidIdx: index('utenti_sync_xid_idx').on(table.sync_xid, table.user_id),
    utentiUpdatedAtIdx: index('utenti_updated_at_idx').on(table.updated_at),
    utentiDeletedAtIdx: index('utenti_deleted_at_idx')
        .on(table.deleted_at)
        .where(sql `${table.deleted_at} IS NOT NULL`),
}));
export const utentiSelectSchema = createSelectSchema(utenti).omit({
    sync_xid: true,
});
export const committenti = pgTable('committenti', {
    user_id: varchar('user_id').primaryKey(),
    username: varchar('username').notNull().unique(),
    company_name: varchar('company_name').notNull(),
    created_at: timestamp('created_at', { withTimezone: true })
        .defaultNow()
        .notNull(),
    updated_at: timestamp('updated_at', { withTimezone: true }).notNull(),
    deleted_at: timestamp('deleted_at', { withTimezone: true }),
    deactivated_at: timestamp('deactivated_at', { withTimezone: true }),
    sync_xid: syncXid(),
}, (table) => ({
    committentiSyncXidIdx: index('committenti_sync_xid_idx').on(table.sync_xid, table.user_id),
    committentiUpdatedAtIdx: index('committenti_updated_at_idx').on(table.updated_at),
    committentiDeletedAtIdx: index('committenti_deleted_at_idx')
        .on(table.deleted_at)
        .where(sql `${table.deleted_at} IS NOT NULL`),
}));
export const committentiSelectSchema = createSelectSchema(committenti).omit({
    sync_xid: true,
});
export const sync_requests = pgTable('sync_requests', {
    id: varchar('id')
        .primaryKey()
        .$defaultFn(() => createId()),
    created_at: timestamp('created_at', { withTimezone: true })
        .defaultNow()
        .notNull(),
    direction: syncDirectionEnum('direction').notNull(),
    model_name: varchar('model_name').notNull(),
    user_id: varchar('user_id'),
    records: jsonb('records').notNull(),
    record_count: integer('record_count').notNull().default(0),
    checkpoint_before: timestamp('checkpoint_before', { withTimezone: true }),
    checkpoint_after: timestamp('checkpoint_after', { withTimezone: true }),
    // The procedure that served the request, so calls from app versions before
    // K35 (pullChanges) can be counted, and sync.pull's cursors
    via: varchar('via'),
    cursor_before: jsonb('cursor_before'),
    cursor_after: jsonb('cursor_after'),
    // The app build that made the request (its x-app-build header), so the
    // builds still in use can be counted; NULL from builds that don't send it
    app_build: varchar('app_build'),
}, (table) => ({
    syncRequestsCreatedAtIdx: index('sync_requests_created_at_idx').on(table.created_at),
}));
export const syncRequestsSelectSchema = createSelectSchema(sync_requests);
export const syncRequestsInsertSchema = createInsertSchema(sync_requests);
